import { motion } from 'framer-motion';
import {
  Target,
  Heart,
  Lightbulb,
  CheckCircle,
  ArrowRight,
  Users,
  Zap,
  Compass,
  AlertCircle,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

export default function MissionStatementPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const missionComponents = [
    {
      icon: Target,
      title: 'What You Do',
      description: 'The products or services you provide—your core business activities',
      example: 'Deliver cloud-based strategic planning software',
      questions: [
        'What products/services do we offer?',
        "What's our core business?",
        'What do customers buy from us?',
      ],
    },
    {
      icon: Users,
      title: 'Who You Serve',
      description: 'Your target customers, beneficiaries, or stakeholders',
      example: 'For mid-market companies and growing startups',
      questions: [
        'Who are our primary customers?',
        'What segments do we focus on?',
        'Who benefits from what we do?',
      ],
    },
    {
      icon: Zap,
      title: 'How You Do It',
      description: 'Your unique approach, methodology, or competitive advantage',
      example: 'Through AI-powered guidance and proven frameworks',
      questions: [
        'What makes our approach unique?',
        'How do we deliver value differently?',
        "What's our competitive edge?",
      ],
    },
    {
      icon: Heart,
      title: 'Why It Matters',
      description: 'The value, impact, or change you create in the world',
      example: 'To help leaders turn strategy into execution',
      questions: [
        'What impact do we create?',
        'What problem do we solve?',
        'Why should anyone care?',
      ],
    },
  ];

  const examples = [
    {
      company: 'Patagonia',
      mission:
        'Build the best product, cause no unnecessary harm, use business to inspire and implement solutions to the environmental crisis',
      breakdown: 'What: build products | How: sustainably | Why: solve environmental crisis',
      why: 'Combines product excellence with environmental activism—differentiates them in outdoor gear market',
    },
    {
      company: 'Google',
      mission: "Organize the world's information and make it universally accessible and useful",
      breakdown:
        'What: organize information | Who: everyone (universal) | Why: accessibility and utility',
      why: 'Broad but clear scope that allows innovation across search, maps, Gmail, YouTube while staying focused on information',
    },
    {
      company: 'Starbucks',
      mission:
        'To inspire and nurture the human spirit – one person, one cup and one neighborhood at a time',
      breakdown:
        'What: inspire and nurture | How: through coffee experiences | Who: individuals and communities',
      why: 'Elevates coffee from commodity to experience—justifies premium pricing and community focus',
    },
    {
      company: 'Tesla',
      mission: "To accelerate the world's transition to sustainable energy",
      breakdown:
        'What: accelerate transition | How: through electric vehicles and energy products | Why: sustainability',
      why: 'Mission goes beyond "make cars"—it\'s about systemic change, which attracts talent and customers who share the mission',
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Foundation', path: '/foundation' },
              { label: 'Mission Statement' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Compass className="w-4 h-4 mr-2" />
            Foundation Step 2 of 4
          </span>
        }
        title="A mission that explains why you matter"
        subtitle="Get clear on who you serve, the value you create, and how you deliver it. Write a mission statement that gives your team a practical reason behind the work."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Draft your mission
        </Link>
      </PageHero>

      {/* Vision vs Mission */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">
              Connect your future ambition to today’s work
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your vision sets the destination. Your mission explains the work you do now to reach
              it. Keeping the two distinct makes both more useful.
            </p>
          </motion.div>
          <div className="public-card max-w-5xl mx-auto bg-white rounded-xl overflow-hidden border border-gray-200">
            <table className="w-full">
              <thead className="bg-gray-50 text-gray-900">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Aspect</th>
                  <th className="px-6 py-4 text-left font-semibold">Vision</th>
                  <th className="px-6 py-4 text-left font-semibold">Mission</th>
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    aspect: 'Time Horizon',
                    vision: 'Future-focused (5-10+ years)',
                    mission: 'Present-focused (now)',
                  },
                  {
                    aspect: 'Nature',
                    vision: 'Aspirational and inspiring',
                    mission: 'Practical and actionable',
                  },
                  {
                    aspect: 'Question Answered',
                    vision: 'Where are we going?',
                    mission: 'Why do we exist?',
                  },
                  {
                    aspect: 'Focus',
                    vision: 'Destination and impact',
                    mission: 'Purpose and method',
                  },
                  {
                    aspect: 'Example',
                    vision: 'To accelerate sustainable energy (Tesla)',
                    mission: 'Design and manufacture electric vehicles (Tesla)',
                  },
                ].map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-6 py-4 font-semibold text-gray-900">{row.aspect}</td>
                    <td className="px-6 py-4 text-gray-700">{row.vision}</td>
                    <td className="px-6 py-4 text-gray-700">{row.mission}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="public-card max-w-3xl mx-auto mt-8 p-6 bg-blue-50 rounded-xl border border-gray-200"
          >
            <p className="text-gray-700 text-center">
              <span className="font-semibold text-blue-700">Remember:</span> Vision is where you're
              going. Mission is why you exist and what you do today to get there.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Components */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">Four Essential Components of Great Missions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every strong mission statement should answer these four questions clearly
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {missionComponents.map((component, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-white p-8 rounded-xl border border-gray-200"
              >
                <div className="bg-blue-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <component.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-3">{component.title}</h3>
                <p className="text-gray-700 mb-4">{component.description}</p>
                <div className="bg-blue-50 p-3 rounded-lg mb-3">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Example:</span> "{component.example}"
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-900 mb-2">Ask Yourself:</p>
                  <ul className="space-y-1">
                    {component.questions.map((q, i) => (
                      <li key={i} className="text-xs text-gray-600 flex items-start">
                        <CheckCircle className="w-3 h-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                        {q}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Write */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">How to Write Your Mission Statement</h2>
              <p className="text-xl text-gray-600">
                Follow this step-by-step process to craft a compelling mission
              </p>
            </motion.div>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Gather Input from Stakeholders',
                  description:
                    "Your mission shouldn't be written by one person in isolation. Interview founders, leadership, employees, and even customers about why the organization exists and what makes it special.",
                  tip: 'Ask: "If we disappeared tomorrow, what would be lost? What gap would we leave?" The answers reveal your true mission.',
                },
                {
                  step: '2',
                  title: 'Answer the Four Key Questions',
                  description:
                    'Work through What, Who, How, and Why systematically. Write separate answers for each, then combine them into a cohesive statement.',
                  tip: 'Start with Why (purpose) before What (activities). Purpose is more enduring than products, which can evolve.',
                },
                {
                  step: '3',
                  title: 'Draft Multiple Versions',
                  description:
                    "Write 5-10 different versions with varying emphasis, length, and tone. Some will be too long, some too vague, some too operational. That's OK—you're exploring options.",
                  tip: 'Try both short (one sentence) and longer (2-3 sentences) versions. Different contexts may call for different lengths.',
                },
                {
                  step: '4',
                  title: 'Test for Clarity and Memorability',
                  description:
                    'Share drafts with people unfamiliar with your business. Can they understand what you do? Will they remember it? Does it differentiate you from competitors?',
                  tip: 'If people ask "What do you actually do?" your mission is too abstract. If they can\'t repeat it back, it\'s too complex or forgettable.',
                },
                {
                  step: '5',
                  title: 'Refine and Get Buy-In',
                  description:
                    'Incorporate feedback, eliminate jargon and buzzwords, and ensure the final mission resonates across the organization. Leadership must champion it.',
                  tip: 'Test the final mission: Does it guide actual decisions? Could you use it to say "no" to opportunities that don\'t fit? If not, it\'s too generic.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-blue-700">Pro Tip:</span> {item.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Real Examples */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">Mission Statements from Leading Companies</h2>
            <p className="text-xl text-gray-600">Learn from organizations that get it right</p>
          </motion.div>
          <div className="space-y-6 max-w-4xl mx-auto">
            {examples.map((example, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-white p-6 rounded-xl border border-gray-200"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{example.company}</h3>
                <p className="text-lg text-blue-600 italic mb-4 leading-relaxed">
                  "{example.mission}"
                </p>
                <div className="bg-blue-50 p-4 rounded-lg mb-3">
                  <p className="text-sm text-gray-700 mb-2">
                    <span className="font-semibold">Breakdown:</span> {example.breakdown}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-green-700">Why It Works:</span>{' '}
                    {example.why}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common Mission Statement Mistakes</h2>
              <p className="text-xl text-gray-600">
                Avoid these pitfalls that make missions forgettable or useless
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Corporate Jargon & Buzzwords',
                  description:
                    '"Leverage synergies to deliver innovative solutions that maximize stakeholder value." This says nothing meaningful. It could describe any company.',
                  fix: 'Use clear, specific language. Avoid: synergy, leverage, solutions, innovative, world-class, cutting-edge, best-in-class.',
                },
                {
                  mistake: 'Too Generic to Be Useful',
                  description:
                    '"To be the leading provider of quality products and services." So vague it provides zero guidance for decision-making.',
                  fix: "Be specific about your business. What products? Which customers? What makes you different? Generic missions don't guide strategy.",
                },
                {
                  mistake: 'Confused with Goals or Vision',
                  description:
                    '"Achieve $100M in revenue by 2028" is a goal, not a mission. "Transform education globally" is vision, not mission.',
                  fix: 'Mission = present purpose. Vision = future aspiration. Goals = measurable targets. Keep them distinct.',
                },
                {
                  mistake: 'Too Long and Complex',
                  description:
                    "Three-paragraph mission statements that no one reads or remembers. If leadership can't recite it, your team definitely can't.",
                  fix: "Aim for 1-2 sentences max. If you need paragraphs to explain it, your mission isn't clear enough.",
                },
                {
                  mistake: 'Not Distinctive',
                  description:
                    'Mission could apply equally well to competitors. "Provide great customer service" doesn\'t differentiate you from anyone else in your industry.',
                  fix: "Your mission should highlight what's unique about your approach, focus, or purpose. If competitors could use the same mission, refine it.",
                },
                {
                  mistake: 'Never Revisited or Updated',
                  description:
                    'Written in 2010 and framed on wall, but business has evolved completely. Mission becomes irrelevant or constraining.',
                  fix: 'Review mission every 3-5 years or when business model significantly changes. Mission can evolve while vision remains constant.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">
                Build a mission around the value you create
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Start with your customer</h3>
                <p className="text-gray-600">
                  Define who depends on your business and the problem you help them solve.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">State your contribution</h3>
                <p className="text-gray-600">
                  Explain the value you deliver in language a customer and a new teammate can
                  understand.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Use it as a filter</h3>
                <p className="text-gray-600">
                  Compare new opportunities with your mission before committing time and resources.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Foundation Elements</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/foundation/vision-statement"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Lightbulb className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-blue-600">Vision Statement</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Define where you're headed long-term and what impact you'll create
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/foundation/core-values"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Heart className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-purple-600">Core Values</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Establish the principles and beliefs that guide all decisions
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/foundation/strategic-objectives"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Target className="w-8 h-8 text-indigo-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-indigo-600">Strategic Objectives</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Set measurable goals that bring your mission to life
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/foundation/vision-statement"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Previous: Vision Statement
              </Link>
              <Link
                to="/foundation/core-values"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next: Core Values <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">Draft your mission in StratPlan</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Compare new opportunities with your mission before committing time and resources. Start
            with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Draft your mission <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
